import { useMemo } from "react";
import { ModeSelector } from "../../components/ModeSelector";
import { NumericInput } from "../../components/NumericInput";
import { ResultPanel } from "../../components/ResultPanel";
import { copy } from "../../data/i18n";
import { decimalsFor, toDisplayValue, unitFor } from "../../data/units";
import type { Language, UnitSystem } from "../../types/preferences";
import { calculateWeldParameter } from "./formulas";
import { modes } from "./presets";
import type { CalculationMode, WeldParameterInputs } from "./types";

const inputMeta: Record<keyof WeldParameterInputs, { mode: CalculationMode }> = {
  aMass: { mode: "aMass" },
  wireDiameter: { mode: "wireDiameter" },
  wireFeed: { mode: "wireFeed" },
  travelSpeed: { mode: "travelSpeed" }
};

type WeldParameterCalculatorProps = {
  language: Language;
  unitSystem: UnitSystem;
  mode: CalculationMode;
  inputs: WeldParameterInputs;
  inputText: Record<keyof WeldParameterInputs, string>;
  onModeChange: (mode: CalculationMode, calculatedKey?: keyof WeldParameterInputs, value?: number) => void;
  onInputTextChange: (key: keyof WeldParameterInputs, value: string) => void;
};

export function WeldParameterCalculator({
  language,
  unitSystem,
  mode,
  inputs,
  inputText,
  onModeChange,
  onInputTextChange
}: WeldParameterCalculatorProps) {
  const t = copy[language];
  const locale = language === "de" ? "de-DE" : "en-US";

  const result = useMemo(() => calculateWeldParameter(mode, inputs), [mode, inputs]);
  const activeMode = modes.find((definition) => definition.mode === mode);
  const calculatedKey = Object.entries(inputMeta).find(([, meta]) => meta.mode === mode)?.[0] as
    | keyof WeldParameterInputs
    | undefined;

  const displayedInputs = {
    ...inputs,
    ...(calculatedKey ? { [calculatedKey]: result.value } : {})
  };

  function changeMode(nextMode: CalculationMode) {
    onModeChange(nextMode, calculatedKey, result.value);
  }

  return (
    <section className="calculator-panel" aria-label="Hauptrechner">
      <div className="calculator-heading">
        <div>
          <h2>{activeMode ? t.modes[activeMode.mode].label : ""}</h2>
          <span>{activeMode ? t.modes[activeMode.mode].description : ""}</span>
        </div>
      </div>

      <div className="target-section">
        <div className="section-label">{t.targetSelection}</div>
        <ModeSelector
          modes={modes}
          activeMode={mode}
          language={language}
          unitSystem={unitSystem}
          onChange={changeMode}
        />
      </div>

      <div className="calculator-body">
        <div className="parameter-section">
          <div className="section-label">{t.inputParameters}</div>
          <div className="input-grid">
            {(Object.keys(inputMeta) as Array<keyof WeldParameterInputs>).map((key) => {
              const meta = inputMeta[key];
              const isResult = meta.mode === mode;
              const displayValue = toDisplayValue(key, displayedInputs[key], unitSystem);
              const decimals = decimalsFor(key, unitSystem);

              return (
                <NumericInput
                  key={key}
                  label={t.inputs[key]}
                  shortLabel={t.modes[meta.mode].shortLabel}
                unit={unitFor(key, unitSystem)}
                value={displayValue}
                textValue={inputText[key]}
                decimals={decimals}
                locale={locale}
                disabled={isResult}
                onChange={(value) => onInputTextChange(key, value)}
              />
              );
            })}
          </div>
        </div>

        <ResultPanel result={result} language={language} unitSystem={unitSystem} />
      </div>
    </section>
  );
}
